/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Link, Typography } from '@mui/material';
import styled from 'styled-components';

import { withStore } from '../../store';
import { addStagingAttachments, clearStagingAttachments, removeStagingAttachments } from '../../store/attachments';
import AttachmentList from '../AttachmentList';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UploadInput = styled.input`
  display: none;
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style-type: none;
  gap: var(--corner-radius-lg);
  display: flex;
  flex-direction: column;
`;

const Item = styled.li`
  padding: 0;
`;

const Bold = styled.span`
  font-weight: bold;
  color: var(--emphasis-ruby-red-500);
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: var(--emphasis-ruby-red-500);
`;

const specialCharactersRegex = /[%&+,]/;

interface UploadDialogSubtitleProps {
  isRequired?: boolean;
}

interface UploadTaxExemptDialogProps {
  store: any;
  dispatch: (action: any) => void;
}

/* ----------------------------------
   Subtitle
---------------------------------- */

export function UploadDialogSubtitle({ isRequired }: UploadDialogSubtitleProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <List>
      {isRequired && (
        <Item>
          <Bold>{t('dialogs.upload.required')}</Bold> {t('dialogs.upload.requiredMessage')}{' '}
          <Link href="https://apps.irs.gov/app/eos/">https://apps.irs.gov/app/eos/</Link>
        </Item>
      )}

      <Item>{t('dialogs.upload.uploadFileTypeMessage')}</Item>
    </List>
  );
}

/* ----------------------------------
   Component
---------------------------------- */

function UploadTaxExemptDialog({ store, dispatch }: UploadTaxExemptDialogProps): JSX.Element {
  const { t } = useTranslation();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(clearStagingAttachments());

    Array.from(e.target.files || []).forEach(file => {
      if (file.name.match(specialCharactersRegex)) {
        (file as any).hasSpecialCharacters = true;
      }
    });

    dispatch(addStagingAttachments(e.target.files));
  };

  const { staged } = store.attachments;
  const { customer: { id: hcId = '' } = {} } = store;

  return (
    <Wrapper>
      <Box
        className="sprout-fileupload drag-and-drop"
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: '8px', sm: '16px' },
          padding: { xs: '15px 0', sm: '31px 0' },
          borderRadius: 'var(--corner-radius-base, 8px)',
          border: '1px dashed var(--Border-border-primary, #DBDDDC)',
          background: 'var(--Background-bg-surface, #FFF)'
        }}
      >
        <CloudUploadIcon
          id="fileupload-cloud-icon"
          className="sprout-fileupload--icon"
          sx={{ width: 36, height: 36, color: '#707773' }}
        />

        <Box
          className="sprout-form-control--text"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Typography
            className="sprout-fileupload--text"
            variant="body1"
            align="center"
            sx={{ color: 'var(--neutral-500)' }}
          >
            <Link
              component="button"
              type="button"
              className="sprout-fileupload--link"
              onClick={() => document.getElementById('fileUpload')?.click()}
              underline="always"
              sx={{ cursor: 'pointer' }}
            >
              {t('buttons.uploadLink')}
            </Link>
          </Typography>
          <Typography
            className="sprout-fileupload--max-size"
            variant="body2"
            align="center"
            sx={{ color: 'var(--neutral-500)' }}
          >
            {t('buttons.uploadSupportingText')}
          </Typography>
        </Box>
      </Box>
      <UploadInput
        type="file"
        onChange={handleOnChange}
        multiple
        accept=".pdf,.png,.jpeg,.jpg,.xls,.xlsx,.doc,.docx,.tif,.svg"
        id="fileUpload"
        aria-label={t('dialogs.upload.browseForFile')}
      />
      {staged.find((file: any) => file.hasSpecialCharacters) && (
        <ErrorText>
          {t('dialogs.upload.specialCharMessage')}
          <br />
          {t('dialogs.upload.specialCharactersInclude')}
        </ErrorText>
      )}

      {staged && staged.length > 0 && (
        <Box sx={{ marginTop: 'var(--spacing-lg)' }}>
          <AttachmentList
            handleRemove={name => dispatch(removeStagingAttachments(name))}
            handleDownload={() => {}}
            hideDownload={true}
            attachments={staged}
            hcId={hcId}
            label={t('address.taxExempt.attachments.label')}
          />
        </Box>
      )}
    </Wrapper>
  );
}

export default withStore(UploadTaxExemptDialog);
